import React, { useState } from "react";
import { Card, CardContent, Grid } from "@mui/material";
import Api from "../../../Api/Api";
import DataTable from "../../../components/Tables/DataTable";
import Container from "../../../components/Container";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";

const styles = {
  panel: {
    width: "100%",
  },
  title: {
    margin: "0 0 4px 0",
    fontSize: 20,
    color: "#001642",
  },
  subtitle: {
    margin: "0 0 20px 0",
    fontSize: 13,
    color: "#4f5b6f",
  },
  searchRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    marginBottom: 12,
    flexWrap: "wrap",
  },
  input: {
    flex: 1,
    minWidth: 240,
    padding: "8px 12px",
    border: "1px solid rgba(0, 22, 66, 0.25)",
    borderRadius: 6,
    fontSize: 14,
    outline: "none",
    background: "#fff",
  },
  btnPrimary: {
    padding: "8px 16px",
    border: "none",
    borderRadius: 6,
    background: "#1266d3",
    color: "#fff",
    fontSize: 14,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnSecondary: {
    padding: "8px 16px",
    border: "1px solid rgba(0, 22, 66, 0.25)",
    borderRadius: 6,
    background: "#fff",
    color: "#001642",
    fontSize: 14,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  filterRow: {
    display: "flex",
    gap: 6,
    alignItems: "center",
    marginBottom: 20,
    flexWrap: "wrap",
  },
  filterLabel: {
    fontSize: 13,
    color: "#4f5b6f",
    marginRight: 4,
  },
  filterBtn: (active) => ({
    padding: "5px 14px",
    border: active ? "2px solid #1266d3" : "1px solid rgba(0, 22, 66, 0.2)",
    borderRadius: 20,
    background: active ? "#e8f0fe" : "#fff",
    color: active ? "#1266d3" : "#4f5b6f",
    fontSize: 13,
    cursor: "pointer",
    fontWeight: active ? 600 : 400,
  }),
  bottomRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 12,
    flexWrap: "wrap",
    gap: 8,
  },
  resultCount: {
    fontSize: 13,
    color: "#4f5b6f",
  },
  error: {
    padding: "10px 14px",
    background: "#fff3f3",
    border: "1px solid #f5c6cb",
    borderRadius: 6,
    color: "#721c24",
    fontSize: 13,
    marginBottom: 12,
  },
  emptyState: {
    textAlign: "center",
    padding: "32px 0",
    color: "#4f5b6f",
    fontSize: 14,
  },
};

const TABLE_COLUMNS = [
  { Header: "Employee",  accessor: "employeeName" },
  { Header: "Quiz Name", accessor: "quizName" },
  { Header: "Quiz End",  accessor: "quizEnd" },
  { Header: "Video Dur", accessor: "videoDuration" },
  { Header: "Quiz Dur",  accessor: "quizDuration" },
  { Header: "Total Dur", accessor: "totalDuration" },
  // Watch-gate observe column: server-measured watched-time vs real runtime. "✓ 96%" =
  // verified; "8%" = recorded but below threshold; "—" = no watch record (e.g. grandfathered).
  { Header: "Watched",   accessor: "watchLabel" },
  { Header: "Score",     accessor: "score" },
  { Header: "Status",    accessor: "workHoursLabel" },
];

const HOURS_FILTERS = [
  { value: "all",     label: "All" },
  { value: "work",    label: "Work Hours" },
  { value: "outside", label: "Outside Hours" },
];

function TrainingCompletionReport() {
  const [search, setSearch]           = useState("");
  const [hoursFilter, setHoursFilter] = useState("all");
  const [records, setRecords]         = useState([]);
  const [pagination, setPagination]   = useState({ total: 0, page: 1, totalPages: 0 });
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  async function handleSearch() {
    const term = search.trim();
    if (term.length < 2) {
      setError("Enter at least 2 characters to search.");
      return;
    }
    setError("");
    setLoading(true);
    setHasSearched(true);
    try {
      const res = await Api.getTrainingCompletionReport(term, 1, 200, hoursFilter);
      setRecords(res.data.records || []);
      setPagination(res.data.pagination || { total: 0 });
    } catch (err) {
      setError("Failed to load report. Please try again.");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  function handleClear() {
    setSearch("");
    setRecords([]);
    setPagination({ total: 0 });
    setError("");
    setHasSearched(false);
  }

  function handleFilterChange(val) {
    setHoursFilter(val);
    // Re-fetch with new filter if already searched
    if (hasSearched && search.trim().length >= 2) {
      const term = search.trim();
      setLoading(true);
      Api.getTrainingCompletionReport(term, 1, 200, val)
        .then(res => {
          setRecords(res.data.records || []);
          setPagination(res.data.pagination || { total: 0 });
        })
        .catch(() => setError("Failed to load report. Please try again."))
        .finally(() => setLoading(false));
    }
  }

  function exportCsv() {
    if (records.length === 0) return;
    const headers = [
      "Employee", "Quiz Name", "Score",
      "Video Start", "Video End", "Quiz Start", "Quiz End",
      "Video Dur", "Quiz Dur", "Total Dur", "Status",
      "Watched Sec", "Video Sec", "Watched %", "Watch Verified"
    ];
    const rows = records.map(r => [
      r.employeeName, r.quizName, r.score ?? "",
      r.videoStart, r.videoEnd, r.quizStart, r.quizEnd,
      r.videoDuration, r.quizDuration, r.totalDuration, r.workHoursLabel,
      r.watchedSeconds ?? "", r.videoDurationSeconds ?? "",
      r.watchedRatio != null ? Math.round(r.watchedRatio * 100) : "",
      r.watchVerified ? "yes" : "no"
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(f => `"${String(f ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `training_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Container title="Training Report" spacing>
        <Grid item xs={12} md={10}>
          <Card>
            <CardContent>
    <div style={styles.panel}>
      <h2 style={styles.title}>Training Completion Report</h2>
      <p style={styles.subtitle}>
        Search by employee name or email to view training history, session times, and work-hours status.
      </p>

      <div style={styles.searchRow}>
        <input
          style={styles.input}
          type="text"
          placeholder="Search by name or email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSearch()}
          disabled={loading}
        />
        <button style={styles.btnPrimary} onClick={handleSearch} disabled={loading}>
          {loading ? "Loading..." : "Search"}
        </button>
        <button style={styles.btnSecondary} onClick={handleClear} disabled={loading}>
          Clear
        </button>
      </div>

      <div style={styles.filterRow}>
        <span style={styles.filterLabel}>Filter:</span>
        {HOURS_FILTERS.map(f => (
          <button
            key={f.value}
            style={styles.filterBtn(hoursFilter === f.value)}
            onClick={() => handleFilterChange(f.value)}
            disabled={loading}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {!hasSearched && !loading && (
        <div style={styles.emptyState}>
          Enter an employee name or email above to view their training completion records.
        </div>
      )}

      {hasSearched && !loading && records.length === 0 && !error && (
        <div style={styles.emptyState}>No records found for that search.</div>
      )}

      {records.length > 0 && (
        <>
          <DataTable
            table={{ columns: TABLE_COLUMNS, rows: records }}
            entriesPerPage={{ defaultValue: 25, entries: [10, 25, 50, 100] }}
            showTotalEntries
            isSorted
            noEndBorder
          />
          <div style={styles.bottomRow}>
            <button
              style={styles.btnSecondary}
              onClick={exportCsv}
              disabled={records.length === 0}
            >
              Export CSV
            </button>
            <span style={styles.resultCount}>
              {pagination.total} total record{pagination.total !== 1 ? "s" : ""}
            </span>
          </div>
        </>
      )}
    </div>
            </CardContent>
          </Card>
        </Grid>
      </Container>
      <Footer />
    </DashboardLayout>
  );
}

export default TrainingCompletionReport;
